import { FC, Fragment, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  MultiSelect,
  Separator,
  NumberInput,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { HOST_GROUPS, IHostGroupsResponse } from 'schemas';
import { SettingsContext } from 'settings';
import { INormalizedTariff } from 'types/tariff';

import s from './PriceList.module.scss';

export interface IPriceListValue {
  host_groups: Array<number> | null;
  cost: number | '';
  user_cost: number | '';
}

interface IPriceListProps {
  control: Control<INormalizedTariff>;
}

export const DEFAULT_PRICE_LIST_VALUE: IPriceListValue = {
  host_groups: null,
  cost: '',
  user_cost: '',
};

export const PriceList: FC<IPriceListProps> = ({ control }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const { data } = useQuery<IHostGroupsResponse>(HOST_GROUPS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const priceListValue = useWatch({ control, name: 'price_list' });
  const hiddenOptions = priceListValue.flatMap(
    (value) => value.host_groups ?? []
  );

  const options = useMemo(
    () =>
      data?.hostGroups.map(({ title, id }) => ({
        title,
        value: id,
      })) ?? [],
    [data]
  );

  const { fields, append, remove } = useFieldArray<
    INormalizedTariff,
    'price_list'
  >({
    control,
    name: 'price_list',
  });

  return (
    <div className={s.container}>
      {fields.map((item, index) => (
        <Fragment key={item.id}>
          {index !== 0 && <Separator />}
          <div className={s['form-block']}>
            <div className={cn(s.multi_block, fields.length > 1 && s.small)}>
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelect
                    {...field}
                    variant="secondary"
                    error={error?.message}
                    title={t('tariff.groups.select')}
                    hiddenOptions={hiddenOptions}
                    className={cn(s.field, fields.length > 1 && s.small)}
                    options={options}
                    bordered
                  />
                )}
                defaultValue={item.host_groups}
                name={`price_list.${index}.host_groups`}
                rules={{ required: t('errors.required') }}
              />
              {fields.length > 1 && (
                <Button
                  variant="ghost"
                  onClick={() => remove(index)}
                  className={cn(s['remove-button'])}
                  iconName="CloseIcon"
                />
              )}
            </div>
            <div className={s.prices}>
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumberInput
                    {...field}
                    title={t('tariff.prices.all')}
                    error={error?.message}
                    placeholder="100"
                    rightContent={currency.sign}
                    bordered
                  />
                )}
                defaultValue=""
                rules={{
                  required: t('errors.required'),
                  min: {
                    value: 0,
                    message: t('errors.min', `0 ${currency.sign}`),
                  },
                  max: {
                    value: 16000000,
                    message: t('errors.max', '16 000 000', currency.sign),
                  },
                }}
                name={`price_list.${index}.cost`}
              />
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumberInput
                    {...field}
                    title={t('tariff.prices.clients')}
                    placeholder="100"
                    error={error?.message}
                    rightContent={currency.sign}
                    bordered
                  />
                )}
                defaultValue=""
                name={`price_list.${index}.user_cost`}
                rules={{
                  min: {
                    value: 0,
                    message: t('errors.min', `0 ${currency.sign}`),
                  },
                  max: {
                    value: 16000000,
                    message: t('errors.max', '16 000 000', currency.sign),
                  },
                }}
              />
            </div>
          </div>
        </Fragment>
      ))}
      <Button
        variant="secondary"
        onClick={() => append({ ...DEFAULT_PRICE_LIST_VALUE })}
        className={s['add-button']}
        iconName="CirclePlusIcon"
      >
        {t('add')}
      </Button>
    </div>
  );
};
